import { logger } from "../../utils/logger";
import { sanitizeMjcfName } from "../../utils/mjcfNames";
import type { Joint, Tendon } from "../../types/scene";
import type { TendonCoefficient } from "./xmlGenerationTypes";
import type { SpatialTendonDef } from "./xmlGenerationTypes";

export type OriginalXmlTendonExportState = {
  tendonCoefficients: Map<string, TendonCoefficient[]>;
  tendonAttributesByName: Map<string, Record<string, string>>;
  tendonDefaultAttributesByClass: Map<string, Record<string, string>>;
  preservedDefaultBlockXml: string | null;
};

export const parseOriginalXmlTendonExportState = async (
  xmlFile: File | null
): Promise<OriginalXmlTendonExportState> => {
  const tendonCoefficients = new Map<string, TendonCoefficient[]>();
  const tendonAttributesByName = new Map<string, Record<string, string>>();
  const tendonDefaultAttributesByClass = new Map<string, Record<string, string>>();
  let preservedDefaultBlockXml: string | null = null;

  if (!xmlFile) {
    return {
      tendonCoefficients,
      tendonAttributesByName,
      tendonDefaultAttributesByClass,
      preservedDefaultBlockXml,
    };
  }

  try {
    const originalXmlText = await xmlFile.text();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(originalXmlText, "application/xml");
    const serializer = new XMLSerializer();
    const defaultElem = xmlDoc.querySelector("mujoco > default");
    if (defaultElem) {
      preservedDefaultBlockXml = serializer.serializeToString(defaultElem).trim();
    }
    xmlDoc.querySelectorAll("default[class] > tendon").forEach((tendonDefault) => {
      const className = tendonDefault.parentElement?.getAttribute("class");
      if (!className) return;
      const attrs: Record<string, string> = {};
      Array.from(tendonDefault.attributes || []).forEach((attr) => {
        if (!attr?.name) return;
        attrs[attr.name] = attr.value;
      });
      tendonDefaultAttributesByClass.set(className, attrs);
    });
    const tendonSection = xmlDoc.querySelector("tendon");
    if (tendonSection) {
      const readTendonAttributes = (node: Element): Record<string, string> | undefined => {
        const attrs: Record<string, string> = {};
        Array.from(node.attributes || []).forEach((attr) => {
          if (!attr?.name || attr.name === "name") return;
          attrs[attr.name] = attr.value;
        });
        return Object.keys(attrs).length > 0 ? attrs : undefined;
      };
      tendonSection.querySelectorAll("fixed").forEach((fixed) => {
        const tendonName = fixed.getAttribute("name");
        if (tendonName) {
          const joints: TendonCoefficient[] = [];
          fixed.querySelectorAll("joint").forEach((jointEl) => {
            const jointName = jointEl.getAttribute("joint");
            const coefStr = jointEl.getAttribute("coef");
            if (jointName && coefStr) {
              const coef = Number(coefStr);
              if (!Number.isNaN(coef)) {
                joints.push({ joint: jointName, coef });
              }
            }
          });
          if (joints.length > 0) {
            tendonCoefficients.set(tendonName, joints);
            const attrs = readTendonAttributes(fixed);
            if (attrs) tendonAttributesByName.set(tendonName, attrs);
          }
        }
      });
    }
  } catch (error) {
    logger.warn("[XML Gen] Failed to parse original XML for tendon coefficients:", error);
  }

  return {
    tendonCoefficients,
    tendonAttributesByName,
    tendonDefaultAttributesByClass,
    preservedDefaultBlockXml,
  };
};

export type SceneTendonExportState = {
  spatialTendonDefs: SpatialTendonDef[];
  spatialReferencedGeomNames: Set<string>;
  tendonUuidToEmittedXmlName: Map<string, string>;
  tendonRefToEmittedXmlName: Map<string, string>;
};

export const supplementTendonExportStateFromScene = ({
  sceneTendons,
  joints,
  tendonCoefficients,
  tendonAttributesByName,
  tendonDefaultAttributesByClass,
  xmlNamingMode,
}: {
  sceneTendons: Record<string, Tendon> | undefined;
  joints: Record<string, Joint>;
  tendonCoefficients: Map<string, TendonCoefficient[]>;
  tendonAttributesByName: Map<string, Record<string, string>>;
  tendonDefaultAttributesByClass: Map<string, Record<string, string>>;
  xmlNamingMode: "readable" | "uuid";
}): SceneTendonExportState => {
  const spatialTendonDefs: SpatialTendonDef[] = [];
  const spatialReferencedGeomNames = new Set<string>();
  const usedTendonXmlNames = new Set<string>();
  const tendonUuidToEmittedXmlName = new Map<string, string>();
  const tendonRefToEmittedXmlName = new Map<string, string>();

  const seedTendonDefaultClassFromAttributes = (
    attributes: Record<string, string> | undefined
  ): void => {
    if (!attributes || typeof attributes !== "object") return;
    const className = attributes.class;
    if (!className || tendonDefaultAttributesByClass.has(className)) return;
    const attrs: Record<string, string> = {};
    Object.entries(attributes).forEach(([key, value]) => {
      if (!key || key === "class" || key === "name" || value === undefined || value === null)
        return;
      attrs[key] = String(value);
    });
    tendonDefaultAttributesByClass.set(className, attrs);
  };

  const reserveTendonXmlName = (
    preferredRaw: string | undefined | null,
    fallbackRaw: string
  ): string => {
    const preferred = sanitizeMjcfName(preferredRaw || "") || sanitizeMjcfName(fallbackRaw);
    let candidate = preferred;
    let suffix = 1;
    while (usedTendonXmlNames.has(candidate)) {
      candidate = sanitizeMjcfName(`${preferred}-${suffix}`);
      suffix += 1;
    }
    usedTendonXmlNames.add(candidate);
    return candidate;
  };

  if (!sceneTendons || typeof sceneTendons !== "object") {
    return {
      spatialTendonDefs,
      spatialReferencedGeomNames,
      tendonUuidToEmittedXmlName,
      tendonRefToEmittedXmlName,
    };
  }

  // Tendon <fixed> coefficients are normally read from `xmlFile`, but that handle is often
  // unset (e.g. library loads, or parseXmlFile clearing it). Parsed tendons still live in
  // scene state, so seed missing entries and keep tendon actuators exportable.
  Object.entries(sceneTendons)
    .sort(([uuidA, tendonA], [uuidB, tendonB]) =>
      String(tendonA?.name || uuidA).localeCompare(String(tendonB?.name || uuidB))
    )
    .forEach(([tendonUuidKey, tendon]) => {
      if (!tendon) return;
      const effectiveKey = reserveTendonXmlName(
        xmlNamingMode === "uuid" ? tendonUuidKey : tendon.name,
        `tendon_${tendonUuidKey}`
      );
      tendonUuidToEmittedXmlName.set(tendonUuidKey, effectiveKey);
      tendonRefToEmittedXmlName.set(tendonUuidKey, effectiveKey);
      tendonRefToEmittedXmlName.set(sanitizeMjcfName(tendonUuidKey), effectiveKey);
      tendonRefToEmittedXmlName.set(effectiveKey, effectiveKey);
      if (tendon.name) {
        const rawTendonName = String(tendon.name);
        tendonRefToEmittedXmlName.set(rawTendonName, effectiveKey);
        tendonRefToEmittedXmlName.set(sanitizeMjcfName(rawTendonName), effectiveKey);
      }
      if (tendon.attributes && typeof tendon.attributes === "object") {
        tendonAttributesByName.set(effectiveKey, tendon.attributes);
        seedTendonDefaultClassFromAttributes(tendon.attributes);
      }
      if (tendon.kind === "spatial") {
        const route = Array.isArray(tendon.spatial)
          ? tendon.spatial
              .map((entry) => {
                if (!entry || typeof entry !== "object") return null;
                if (entry.kind === "site" && entry.site) {
                  return { kind: "site", site: String(entry.site) };
                }
                if (entry.kind === "geom" && entry.geom) {
                  spatialReferencedGeomNames.add(sanitizeMjcfName(String(entry.geom)));
                  const next: { kind: string; geom: string; sidesite?: string } = {
                    kind: "geom",
                    geom: String(entry.geom),
                  };
                  if (entry.sidesite) next.sidesite = String(entry.sidesite);
                  return next;
                }
                if (entry.kind === "pulley") {
                  const divisor = Number(entry.divisor);
                  return {
                    kind: "pulley",
                    ...(Number.isFinite(divisor) ? { divisor } : {}),
                  };
                }
                return null;
              })
              .filter(
                (
                  entry
                ): entry is {
                  kind: string;
                  site?: string;
                  geom?: string;
                  sidesite?: string;
                  divisor?: number;
                } => !!entry
              )
          : [];
        spatialTendonDefs.push({
          uuid: tendonUuidKey,
          name: effectiveKey,
          attributes: tendon.attributes,
          route,
        });
        return;
      }
      if (tendon.kind !== "fixed") return;
      const members: Array<{ joint: string; coef: number }> = [];
      (tendon.joints || []).forEach((ref) => {
        const jid = ref?.joint;
        if (!jid) return;
        const joint = joints[jid as keyof typeof joints];
        if (!joint) return;
        const coefRaw = Number(ref?.coef);
        const coef = Number.isFinite(coefRaw) ? coefRaw : 1;
        members.push({ joint: jid, coef });
      });
      if (members.length >= 2) {
        tendonCoefficients.set(effectiveKey, members);
      }
    });

  return {
    spatialTendonDefs,
    spatialReferencedGeomNames,
    tendonUuidToEmittedXmlName,
    tendonRefToEmittedXmlName,
  };
};
